import { useCallback, useEffect, useMemo, useState } from "react";
import { publicationLabel } from "@/lib/auditSheetLabels";
import TrainingSessionDetailPanel from "@/components/instructor/TrainingSessionDetailPanel";

const PAGE_SIZE = 20;

const FILTER_OPTIONS = [
  { id: "ALL", label: "All" },
  { id: "PUBLISHED", label: "Published" },
  { id: "APPROVED", label: "Approved" },
  { id: "DRAFT", label: "Draft" },
  { id: "QUICK_DAILY_FEEDBACK", label: "Quick Daily Feedback" },
  { id: "DETAILED_ASSESSMENT", label: "Detailed Assessment" },
  { id: "MOCK_READINESS", label: "Mock / Readiness" },
  { id: "QUICK_LESSON", label: "Quick Lesson" },
  { id: "HAS_SKETCH", label: "Has sketch" },
];

function statusClass(state) {
  const pub = publicationLabel(state);
  return pub === "Published" ? "audit-history-badge-published" : "audit-history-badge-published audit-history-badge-draft";
}

export default function TrainingTimeline({
  learnerId,
  programmeId,
  api,
  busy,
  practicalEnabled,
  onTransition,
  onViewSketch,
}) {
  const [sessions, setSessions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState("newest");
  const [filter, setFilter] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [unlinkedSketches, setUnlinkedSketches] = useState([]);
  const [duplicateFlags, setDuplicateFlags] = useState([]);

  const queryParams = useMemo(() => {
    const params = {
      programme_id: programmeId,
      sort,
      limit: PAGE_SIZE,
      offset: 0,
    };
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (filter === "PUBLISHED" || filter === "APPROVED" || filter === "DRAFT") {
      params.publication_state = filter;
    } else if (
      filter === "QUICK_DAILY_FEEDBACK" ||
      filter === "DETAILED_ASSESSMENT" ||
      filter === "MOCK_READINESS" ||
      filter === "QUICK_LESSON"
    ) {
      params.workflow_kind = filter;
    } else if (filter === "HAS_SKETCH") {
      params.has_sketch = true;
    }
    return params;
  }, [programmeId, sort, filter, fromDate, toDate]);

  const loadTimeline = useCallback(
    async (append = false, nextOffset = 0) => {
      if (!learnerId || !programmeId) return;
      setLoading(true);
      setError("");
      try {
        const res = await api.get(`/instructor/learners/${learnerId}/training-timeline`, {
          params: { ...queryParams, offset: nextOffset },
        });
        const batch = res.data.sessions || [];
        setSessions((prev) => (append ? [...prev, ...batch] : batch));
        setTotalCount(res.data.total_count ?? batch.length);
        setOffset(nextOffset + batch.length);
        setUnlinkedSketches(res.data.unlinked_sketches || []);
        setDuplicateFlags(res.data.duplicate_flags || []);
      } catch (err) {
        setError(err?.response?.data?.detail || "Training timeline unavailable.");
        if (!append) setSessions([]);
      } finally {
        setLoading(false);
      }
    },
    [api, learnerId, programmeId, queryParams]
  );

  useEffect(() => {
    setOffset(0);
    setDetailId(null);
    loadTimeline(false, 0);
  }, [loadTimeline]);

  const detailSession = sessions.find((s) => s.record_id === detailId);

  if (!learnerId) {
    return (
      <section className="audit-surface audit-section" data-testid="training-timeline">
        <h2 className="audit-section-title">Training timeline</h2>
        <p className="text-sm mt-2">Select a learner to view training sessions.</p>
      </section>
    );
  }

  return (
    <section className="audit-surface audit-section" aria-label="Training timeline" data-testid="training-timeline">
      <div className="audit-panel-header">
        <h2 className="audit-section-title">Training timeline</h2>
        <span className="text-xs" style={{ color: "rgba(22,25,28,.6)" }}>
          {totalCount} training session{totalCount === 1 ? "" : "s"}
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="timeline-filters">
        {FILTER_OPTIONS.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`audit-btn-compact ${filter === f.id ? "audit-btn-outline-dark" : ""}`}
            style={{ marginLeft: 0 }}
            onClick={() => setFilter(f.id)}
            data-testid={`timeline-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
        <label className="text-xs flex items-center gap-1">
          From
          <input type="date" className="input text-xs py-1" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="timeline-from-date" />
        </label>
        <label className="text-xs flex items-center gap-1">
          To
          <input type="date" className="input text-xs py-1" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="timeline-to-date" />
        </label>
        <button
          type="button"
          className="audit-btn-compact"
          style={{ marginLeft: 0 }}
          onClick={() => setSort((s) => (s === "newest" ? "oldest" : "newest"))}
          data-testid="timeline-sort-toggle"
        >
          {sort === "newest" ? "Newest first" : "Oldest first"}
        </button>
      </div>

      {error && (
        <p role="alert" className="text-destructive text-sm mb-2" data-testid="timeline-error">
          {error}
        </p>
      )}

      {duplicateFlags.length > 0 && (
        <div className="text-xs mb-2 p-2 border rounded" style={{ borderColor: "rgba(198,40,40,.3)" }} data-testid="timeline-duplicate-flags">
          <strong>Review required:</strong> {duplicateFlags.length} possible data-integrity flag
          {duplicateFlags.length === 1 ? "" : "s"} detected (no automatic merge).
        </div>
      )}

      <ul className="space-y-0" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {sessions.map((s) => {
          const pub = publicationLabel(s.publication_state);
          const isOpen = detailId === s.record_id;
          return (
            <li key={s.record_id} className="audit-history-card" data-testid={`timeline-session-${s.record_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-semibold text-sm" data-testid={`timeline-date-${s.record_id}`}>
                    {s.date_nz}
                    {s.time_display ? ` · ${s.time_display}` : ""}
                  </span>
                  <span className="text-xs" style={{ color: "rgba(22,25,28,.6)" }}>
                    {s.workflow_label}
                  </span>
                  <span className={statusClass(s.publication_state)}>{pub}</span>
                  {s.review_required && (
                    <span className="text-xs font-medium" style={{ color: "#c62828" }}>
                      Review required
                    </span>
                  )}
                </div>
                <p className="text-xs" style={{ color: "rgba(22,25,28,.6)" }}>
                  {s.instructor_name || "Instructor"}
                  {s.scored_skills_count > 0 && (
                    <> · {s.scored_skills_count} scored · mean {s.mean_score ?? "—"}</>
                  )}
                  {s.attachment_label && <> · {s.attachment_label}</>}
                </p>
                <p className="text-sm truncate">{s.public_feedback_summary}</p>
                {isOpen && (
                  <TrainingSessionDetailPanel
                    session={s}
                    busy={busy}
                    practicalEnabled={practicalEnabled}
                    onTransition={onTransition}
                    onViewSketch={onViewSketch}
                  />
                )}
              </div>
              <button
                type="button"
                className="audit-btn-compact"
                style={{ marginLeft: 0 }}
                onClick={() => setDetailId(isOpen ? null : s.record_id)}
                data-testid={`timeline-open-${s.record_id}`}
              >
                {isOpen ? "Close" : "Open details"}
              </button>
            </li>
          );
        })}
      </ul>

      {loading && <p className="text-sm mt-2">Loading…</p>}

      {offset < totalCount && !loading && (
        <button
          type="button"
          className="audit-btn-outline-dark mt-3"
          onClick={() => loadTimeline(true, offset)}
          data-testid="timeline-load-more"
        >
          Load more ({offset} of {totalCount})
        </button>
      )}

      {unlinkedSketches.length > 0 && (
        <div className="mt-4 pt-3 border-t" data-testid="timeline-unlinked-sketches">
          <h3 className="text-sm font-semibold mb-2">Unlinked historical sketches</h3>
          <ul className="text-sm space-y-1">
            {unlinkedSketches.map((sk) => (
              <li key={sk.sketch_id}>
                {sk.title || "Sketch"} · {sk.page_count} page{sk.page_count === 1 ? "" : "s"} · rev {sk.revision}
                {sk.saved_at_nz ? ` · ${sk.saved_at_nz}` : ""}
                <span className="text-xs ml-1" style={{ color: "rgba(22,25,28,.5)" }}>
                  ({sk.sketch_id?.slice(0, 8)}…)
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
